  import { useEffect, useMemo, useState, useRef } from "react";
  import { Helmet } from "react-helmet-async";
  import { IconSearch } from "../components/Icons";

  import {
    getRecentBusiness,
    searchBusiness,
    getNearbyBusiness,
  } from "../services/businessService";

  import BusinessCard from "../components/BusinessCard";
  import Pagination from "../components/Pagination";
  import BusinessDetails from "../components/BusinessDetails";

  import { toast } from "react-toastify";
  import { CATEGORIES } from "../constants/categories";
  import { getCategories } from "../services/categoryService";
  import { getCities } from "../services/cityService";

  const Home = () => {
    
    const [cities, setCities] = useState([]);

    // Initialise from URL params so shared/bookmarked links restore their state
    const [query, setQuery] = useState(
      () => new URLSearchParams(window.location.search).get('q') || ''
    );
    const [businessList, setBusinessList] = useState([]);

    const [selectedCity, setSelectedCity] = useState(
      () => new URLSearchParams(window.location.search).get('city') || 'Chennai'
    );
    const [selectedCategory, setSelectedCategory] = useState(
      () => new URLSearchParams(window.location.search).get('cat') || 'All'
    );

    const [useNearby, setUseNearby] = useState(false);

    const [status, setStatus] = useState("");
    const [noResults, setNoResults] = useState(false);

    const [loading, setLoading] = useState(false);

    const [selectedBusiness, setSelectedBusiness] = useState(null);

    const [page, setPage] = useState(
      () => parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1
    );

    // Categories: start with hardcoded fallback, replace with API data when loaded
    const [categories, setCategories] = useState(["All", ...CATEGORIES]);

    const itemsPerPage = 10;

    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    // Skips the first fire of the city/category effect (initial load is handled by the page effect)
    const initialLoad = useRef(true);
    // True while detectLocation is running — tells city/category effect to skip the fetch
    const nearbyInProgress = useRef(false);

    // Sync filter state → URL (no re-render, no API call, just updates browser address bar)
    useEffect(() => {
      const params = new URLSearchParams();
      if (query)                            params.set('q', query);
      if (selectedCity !== 'Chennai')       params.set('city', selectedCity);
      if (selectedCategory !== 'All')       params.set('cat', selectedCategory);
      if (page > 1)                         params.set('page', String(page));
      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }, [query, selectedCity, selectedCategory, page, useNearby]);

    // Load cities from API; dropdown stays empty until response arrives
    useEffect(() => {
      getCities()
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            setCities(data.map((c) => c.name));
          }
        })
        .catch(() => {});
    }, []);

    // Load categories from API; fall back to the hardcoded constant on failure
    useEffect(() => {
      getCategories()
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            const names = data.filter((c) => c.active).map((c) => c.name);
            if (names.length > 0) {
              setCategories(["All", ...names]);
              // If the currently selected category no longer exists, reset to All
              setSelectedCategory((prev) =>
                prev === "All" || names.includes(prev) ? prev : "All"
              );
            }
          }
        })
        .catch(() => {
          // API unavailable — keep the hardcoded fallback already in state
        });
    }, []);

    // ✅ LOAD RECENT BUSINESS
    useEffect(() => {
      if (useNearby) return;  // nearby results are in memory — page nav is client-side
      if (query.trim()) {
        handleSearch(page);
      } else {
        loadRecentBusiness();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // ✅ LOAD RECENT
    const loadRecentBusiness = async (pageOverride) => {
      try {
        setLoading(true);

        const data = await getRecentBusiness(pageOverride ?? page, itemsPerPage, selectedCity, selectedCategory);

        if (data?.data && Array.isArray(data.data)) {
          setBusinessList(data.data);
          setTotalPages(data.pages || 1);
          setTotalCount(data.total || data.data.length);

          setStatus(
            data.data.length
              ? "Showing recent businesses"
              : "No businesses available yet"
          );

          setNoResults(data.data.length === 0);
        } else {
          setBusinessList([]);
          setTotalPages(1);
          setTotalCount(0);

          setStatus("No businesses available");

          setNoResults(true);
        }
      } catch (err) {
        console.log(err);

        setStatus("Unable to load businesses");

        setNoResults(true);
      } finally {
        setLoading(false);
      }
    };

    // ✅ SEARCH BUSINESS
    const handleSearch = async (currentPage = 1) => {
      try {
        setLoading(true);

        setUseNearby(false);

        const data = await searchBusiness(
          query,
          selectedCity,
          currentPage,
          itemsPerPage,
          selectedCategory
        );

        if (data?.data && Array.isArray(data.data)) {
          setBusinessList(data.data);
          setTotalPages(data.pages || 1);
          setTotalCount(data.total || data.data.length);

          setStatus(
            data.data.length
              ? `${data.total || data.data.length} business(es) found in ${selectedCity}`
              : "No business found 😕"
          );

          setNoResults(data.data.length === 0);
        } else {
          setBusinessList([]);
          setTotalPages(1);
          setTotalCount(0);

          setStatus("No businesses found");

          setNoResults(true);
        }
      } catch (err) {
        console.log(err);

        setStatus("Error searching business");

        setNoResults(true);
      } finally {
        setLoading(false);
      }
    };

    // ✅ NEARBY SEARCH
    const handleNearbySearch = async (lat, lng) => {
      try {
        setLoading(true);

        let data = await getNearbyBusiness(lat, lng);

        setUseNearby(true);

        if (!data || data.length === 0) {
          handleResponse(
            [],
            "No nearby businesses found. Try expanding the search."
          );

          return;
        }

        // ✅ VALID DISTANCE ONLY
        data = data.filter(
          (item) =>
            item.distance !== undefined &&
            item.distance !== null &&
            !isNaN(item.distance)
        );

        // ✅ SORT BY DISTANCE
        data.sort((a, b) => a.distance - b.distance);

        // ✅ WITHIN 50KM
        const nearby = data.filter((item) => item.distance <= 50000);

        if (nearby.length === 0) {
          handleResponse(
            [],
            "No nearby businesses found within 50 km."
          );

          return;
        }

        handleResponse(
          nearby,
          `${nearby.length} nearby business(es) within 50 km`
        );
      } catch (err) {
        console.log(err);

        toast.error("Error finding nearby businesses");
      } finally {
        setLoading(false);
      }
    };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }

    setStatus("Getting your location...");
    setLoading(true);

    const getPosition = (isRetry = false) => {
      setStatus("Detecting your location...");

      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          try {
            // Geocoding only — failure skips city update but does NOT block nearby search
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}`
              );

              if (!res.ok) throw new Error("Reverse geocoding failed");

              const data = await res.json();

              let detectedDistrict =
                data.address?.state_district ||
                data.address?.county ||
                data.address?.city ||
                data.address?.town ||
                data.address?.village ||
                "";

              detectedDistrict = detectedDistrict?.replace(/district/i, "").trim();

              if (detectedDistrict) {
                const exists = cities.find(
                  (c) => c.toLowerCase() === detectedDistrict.toLowerCase()
                );

                // Set flag before setSelectedCity so the city/category effect skips
                // its loadRecentBusiness call — handleNearbySearch runs immediately after
                nearbyInProgress.current = true;

                if (exists) {
                  setSelectedCity(exists);
                } else {
                  setCities((prev) =>
                    prev.some(
                      (c) => c.toLowerCase() === detectedDistrict.toLowerCase()
                    )
                      ? prev
                      : [...prev, detectedDistrict]
                  );

                  setSelectedCity(detectedDistrict);
                }
              }
            } catch (geoErr) {
              console.log(geoErr);
              // City stays as current selection; nearby search still runs below
            }

            // Always run nearby search regardless of geocoding outcome
            setStatus("Finding nearby businesses...");
            await handleNearbySearch(coords.latitude, coords.longitude);
            nearbyInProgress.current = false;

          } catch (err) {
            console.log(err);
            toast.error("Unable to detect location");
          } finally {
            setLoading(false);
          }
        },

        (err) => {
          console.log("Geolocation error:", err);

          if (!isRetry && err.code === 3) {
            setTimeout(() => getPosition(true), 1500);
            return;
          }

          if (err.code === 1) {
            toast.error("Location permission denied — allow access in browser settings.");
          } else if (err.code === 2) {
            toast.error("Please enable GPS/location services.");
          } else if (err.code === 3) {
            toast.error("Location request timed out.");
          } else {
            toast.error("Unable to get location.");
          }

          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    };

    getPosition(false);
  };

 


    // ✅ RESPONSE HANDLER
    const handleResponse = (data, message) => {
      setBusinessList(Array.isArray(data) ? data : []);

      setStatus(message);

      setNoResults(!data || data.length === 0);

      setPage(1);
    };

    // ✅ FILTERED BUSINESS
      const filteredBusiness = useMemo(() => {
      const result = businessList.filter((item) => {
        if (!item) return false;

        const matchesSearch =
          item.businessName
            ?.toLowerCase()
            .includes(query.toLowerCase()) ||
          item.category
            ?.toLowerCase()
            .includes(query.toLowerCase()) ||
          item.services
            ?.toLowerCase()
            .includes(query.toLowerCase());

        const matchesCategory =
          selectedCategory === "All" ||
          item.category === selectedCategory;

        const matchesCity =
          useNearby ||
          item.city?.trim().toLowerCase() ===
            selectedCity?.trim().toLowerCase();

        return matchesSearch && matchesCategory && matchesCity;
      });

      // ⭐ IMPORTANT ADDITION: nearest-first sorting
      if (useNearby) {
        return result.sort((a, b) => a.distance - b.distance);
      }

      return result;
    }, [
      businessList,
      query,
      selectedCategory,
      selectedCity,
      useNearby,
    ]);
    

    // Nearby mode: client-side slice of filtered results
    // All other modes: backend already paginated — use businessList directly
    const displayPages = useNearby
      ? Math.ceil(filteredBusiness.length / itemsPerPage)
      : totalPages;

    const paginatedBusiness = useNearby
      ? filteredBusiness.slice((page - 1) * itemsPerPage, page * itemsPerPage)
      : businessList;

    // ✅ RESET PAGE ON QUERY / NEARBY CHANGE
    useEffect(() => {
      setPage(1);
    }, [query, useNearby]);

    // ✅ CITY / CATEGORY CHANGE — reset page and re-fetch
    // useRef guard skips the initial mount (page effect handles that load)
    useEffect(() => {
      if (initialLoad.current) {
        initialLoad.current = false;
        return;
      }
      // detectLocation sets this flag before updating the city — skip the fetch here
      // because handleNearbySearch is about to run and will own the result
      if (nearbyInProgress.current) return;
      setPage(1);
      // Direct fetch covers the case where page was already 1 (setPage(1) is a no-op)
      if (query.trim()) {
        handleSearch(1);
      } else {
        loadRecentBusiness(1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCity, selectedCategory]);

    // ✅ SCROLL TO TOP ON PAGE CHANGE
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page]);

    return (
      <div className="home-container">
        <Helmet>
          <title>BusinessNearMe – Find Local Businesses in Tamil Nadu</title>
          <meta name="description" content={`Discover top-rated shops, services and local businesses in ${selectedCity}, Tamil Nadu. Search by category, location or name.`} />
          <link rel="canonical" href="https://businessnearme.in/" />
          <meta property="og:title" content="BusinessNearMe – Find Local Businesses in Tamil Nadu" />
          <meta property="og:description" content="Search thousands of verified local businesses across 37 districts of Tamil Nadu. Find shops, restaurants, services and more near you." />
          <meta property="og:url" content="https://businessnearme.in/" />
          <meta property="og:type" content="website" />
        </Helmet>

        {/* 🔍 HERO SEARCH PANEL */}
        <div className="control-panel">
          <h1 className="hero-title">Find Local Businesses Near You</h1>
          <p className="hero-subtitle">Search by name, category, or use your current location</p>
          <div className="search-controls">
            <div className="search-input-group">
              <IconSearch size={15} className="search-icon" />

              <input
                type="text"
                placeholder="Search Businesses"
                aria-label="Search businesses by name, category or service"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setPage(1);
                    handleSearch(1);
                  }
                }}
                className="search-input"
              />
            </div>

            <select
              value={selectedCity}
              onChange={(e) => {
                setSelectedCity(e.target.value);

                setUseNearby(false);
              }}
              className="location-select"
            >
              {cities.map((city) => (
                <option key={city}>{city}</option>
              ))}
            </select>

            <button
              onClick={() => handleSearch(1)}
              className="search-btn"
            >
              Search
            </button>

            <button
              onClick={detectLocation}
              className="location-btn"
              disabled={loading}
            >
              {loading ? "Detecting location..." : "Use Current Location"}
            </button>
          </div>
        </div>

        {/* 🧱 CATEGORY FILTER */}
        <div className="category-scroll-wrapper">
          <div className="category-filter">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`category-btn ${selectedCategory === cat ? "active" : ""}`}
                aria-pressed={selectedCategory === cat}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        
        {/* 📢 STATUS */}
        {!loading && businessList.length > 0 && (
          <div className="status-message">
            {useNearby
              ? `${filteredBusiness.length} business${filteredBusiness.length !== 1 ? "es" : ""} near you · ${selectedCity}`
              : `${totalCount} result${totalCount !== 1 ? "s" : ""} in ${selectedCity}`}
          </div>
        )}


        {/* ⏳ LOADING */}
        {loading && (
          <div className="loading-message">
            <span className="loading-spinner" />
            {status || "Loading..."}
          </div>
        )}

        {/* ❌ NO RESULTS */}
        {!loading && noResults ? (
          <div className="empty-state">
            <h3>No businesses found</h3>
            <p>Try adjusting your search or selecting a different city.</p>
          </div>
        ) : (
          <>
            {/* 🛍 BUSINESS GRID */}
            <div className="product-grid">
              {paginatedBusiness.map((item, index) =>
                item ? (
                  <BusinessCard
                    key={item._id || index}
                    business={item}
                    onSelect={setSelectedBusiness}
                  />
                ) : null
              )}
            </div>

            {/* ✅ PAGINATION */}
            {displayPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={page}
                  totalPages={displayPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}

        {/* ✅ BUSINESS DETAILS */}
        {selectedBusiness && (
          <BusinessDetails
            business={selectedBusiness}
            onClose={() =>
              setSelectedBusiness(null)
            }
          />
        )}
      </div>
    );
  };

  export default Home;